import {
  buildProgressAssessmentUnits,
  collectProgressSubmissionEnrolmentIds,
  fetchProgressSubmissionData,
  loadProgressAssignmentContext
} from './studentProgressAssessmentUnits';
import { getEffectiveToday } from './systemTime';

function publishedFeedback(submission) {
  const source = submission?.assignment_feedback;
  const rows = Array.isArray(source) ? source : source ? [source] : [];

  return rows
    .filter(row => row?.submitted === true)
    .sort((a, b) =>
      new Date(b.submitted_at || b.updated_at || 0).getTime() -
      new Date(a.submitted_at || a.updated_at || 0).getTime()
    )[0] || null;
}

export async function loadMeetingAssignmentOverview(courseId, enrolments) {
  const students = (Array.isArray(enrolments) ? enrolments : [])
    .filter(row => row?.id)
    .map(row => ({ ...row, course_id: row.course_id || courseId }));

  if (!students.length) return new Map();

  const context = await loadProgressAssignmentContext({
    courseId,
    enrolments: students
  });

  const ids = collectProgressSubmissionEnrolmentIds({
    enrolments: students,
    context
  });

  const { submissions, completions } =
    await fetchProgressSubmissionData(ids);

  const displayNames = new Map(
    (context.assignmentTypeSettings || []).map(row => [
      String(row.assignment_type_id),
      String(row.display_name || '').trim()
    ])
  );

  return new Map(students.map(enrolment => {
    const units = buildProgressAssessmentUnits({
      courseId,
      enrolment,
      context,
      submissions,
      completions
    });

    const today = getEffectiveToday();

    const assignmentRows = units
      .filter(unit => {
        const dueDate =
          String(unit.dueDate || '').slice(0, 10);

        const due =
          !!dueDate && dueDate <= today;

        const alreadySubmitted =
          unit.submitted === true ||
          !!publishedFeedback(unit.submission);

        const alreadyCompleted =
          unit.completed === true;

        return due || alreadySubmitted || alreadyCompleted;
      })
      .map(unit => {
      const assignment = unit.assignment || {};
      const baseName =
        displayNames.get(String(unit.assignmentTypeId)) ||
        unit.typeName ||
        'Assignment';

      const number = Number(assignment.instance_number) || 1;
      const title = number > 1 ? baseName + ' ' + number : baseName;

      const detail = String(unit.detailLabel || '').trim();
      const label = detail && !['Assignment', 'Completion'].includes(detail)
        ? title + ' — ' + detail
        : title;

      const feedback = publishedFeedback(unit.submission);
      const submitted = unit.submitted === true || !!feedback;

      let status;

      if (unit.requiresSubmission === false) {
        status = unit.completed ? 'Complete' : 'Not complete';
      } else if (submitted) {
        status = String(feedback?.completion_status || '').trim() ||
          (feedback ? 'Graded' : 'Submitted');
      } else if (unit.choiceMissing) {
        status = 'Schedule not chosen';
      } else {
        status = 'Not submitted';
      }

      return {
        label,
        status,
        instructorNote: feedback?.instructor_note || null
      };
    });

    return [String(enrolment.id), assignmentRows];
  }));
}
